import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { CaretDown, Note } from 'phosphor-react';

/** Largura em "caracteres médios": maiúscula ocupa mais que minúscula. */
const larguraEstimada = (texto: string): number =>
  Array.from(texto).reduce((t, c) => t + (c !== c.toLowerCase() ? 1.5 : 1), 0);

interface FaixaObservacaoProps {
  obs: string;
  /** Quem escreveu o último comentário; na observação antiga, o responsável do processo. */
  autor?: string | null;
  /** Expandida? O estado mora no BOARD (o dnd remonta o card no arraste). */
  aberta: boolean;
  onToggle: () => void;
}

/**
 * OBSERVAÇÃO — recado que vale pro processo inteiro, não pra etapa. Mostra o último
 * comentário do card, com o primeiro nome de quem escreveu (o nome completo e o
 * histórico ficam no drawer).
 * Colapsada em uma linha por padrão: card de quadro é feito pra varredura, e obs
 * longa em card sempre aberto empurraria o rodapé de todo mundo pra baixo.
 *
 * ⚠️ Vira <button> de verdade quando expansível, por dois motivos: o teclado
 * precisa alcançar o controle, e o @hello-pangea/dnd NÃO inicia arrasto a partir de
 * elemento interativo — apertar sobre a obs alterna, não arrasta. (O card em volta
 * continua sendo a alça; por isso ele é um Box com role, e não um <button>, senão o
 * arrasto morreria no card inteiro.) O stopPropagation impede que o mesmo clique
 * abra o drawer.
 */
export const FaixaObservacao: React.FC<FaixaObservacaoProps> = ({
  obs: texto,
  autor = null,
  aberta,
  onToggle,
}) => {
  const quem = autor?.trim().split(/\s+/)[0] || null;
  // Menção do comentário (`@[Nome](id:12)`) aparece como no comentário: "@Nome".
  const obs = texto.replace(/@\[([^\]]+)\]\(id:\d+\)/g, '@$1');
  /**
   * Texto que já cabe inteiro na linha não vira botão.
   *
   * Um chevron que expande e não revela nada é promessa falsa — em quadro com muitos
   * cards, o operador aprende a ignorar o controle. O corte é heurístico e erra pro lado
   * seguro: na dúvida ele mostra o controle, e o pior caso é uma expansão que não
   * acrescenta linha. Medir de verdade custaria layout por card a cada render, no mesmo
   * caminho que já engasgava a rolagem.
   *
   * Medido em 29/09 no card real (coluna de 248px, texto a 11px): cabem ~31 caracteres
   * de texto corrido, não os 38 de antes — obs de 32 a 38 saía cortada e sem controle.
   * Maiúscula é mais larga (pesa 1,5) e o nome em negrito também (1,1).
   */
  const curta =
    !obs.includes('\n') &&
    (quem ? larguraEstimada(quem) * 1.1 + 2 : 0) + larguraEstimada(obs) <= 26;
  const expansivel = !curta;

  return (
    <Flex
      {...(expansivel
        ? {
            as: 'button' as const,
            type: 'button' as const,
            onClick: (e: React.MouseEvent) => {
              e.stopPropagation();
              onToggle();
            },
            // ⚠️ O stopPropagation do CLIQUE não basta. O card em volta escuta
            // `onKeyDown` e abre o drawer no Enter — e o keydown bubbla por conta
            // própria, antes de virar clique. Sem isto, Enter com o foco na obs
            // alternava E abria o drawer no mesmo gesto. (Espaço é do dnd, que já
            // recusa este elemento por ser interativo.)
            onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation(),
            'aria-expanded': aberta,
            'aria-label': aberta ? 'Recolher observação' : 'Expandir observação',
            _hover: { bg: 'blackAlpha.100' },
            cursor: 'pointer',
          }
        : {})}
      w="100%"
      textAlign="left"
      align="flex-start"
      gap={1.5}
      px={1.5}
      py={1}
      borderRadius="md"
      bg="blackAlpha.50"
      transition="background .12s"
    >
      {/* `as="span"` nos três: quando a faixa é <button>, o conteúdo tem que ser
          phrasing content — <p> (o padrão do Text) e <div> dentro de <button> são HTML
          inválido. */}
      <Box as="span" display="flex" mt="1px" flexShrink={0}>
        <Note size={11} color="#94A3B8" weight="duotone" />
      </Box>
      <Text
        as="span"
        fontSize="11px"
        color="gray.600"
        lineHeight="1.4"
        flex="1"
        minW={0}
        whiteSpace="pre-wrap"
        {...(aberta && expansivel ? { display: 'block' } : { noOfLines: 1 })}
      >
        {quem && (
          <Text as="span" fontWeight="600" color="gray.700">
            {quem}:{' '}
          </Text>
        )}
        {obs}
      </Text>
      {expansivel && (
        <Box
          as="span"
          display="flex"
          mt="1px"
          flexShrink={0}
          transform={aberta ? 'rotate(180deg)' : undefined}
          transition="transform .15s"
        >
          <CaretDown size={10} color="#94A3B8" weight="bold" />
        </Box>
      )}
    </Flex>
  );
};
